import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Languages, Save, Search, Check, AlertCircle, Loader2, 
  RefreshCw, ChevronDown, ChevronUp, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import FlagIcon from '../../components/FlagIcon';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminTranslations = () => {
  const { token } = useAdminAuth();
  const [translations, setTranslations] = useState([]);
  const [categories, setCategories] = useState({});
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('navbar');
  const [searchQuery, setSearchQuery] = useState('');
  const [editedTranslations, setEditedTranslations] = useState({});
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [transRes, catRes, langRes] = await Promise.all([
        axios.get(`${API}/api/translations`),
        axios.get(`${API}/api/translations/categories`),
        axios.get(`${API}/api/admin/languages`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => axios.get(`${API}/api/languages`))
      ]);
      
      setTranslations(transRes.data);
      setCategories(catRes.data);
      
      const langs = langRes.data.map(l => ({
        code: l.code,
        name: l.nativeName || l.name,
        flag: l.flag,
        isActive: l.isActive !== false
      }));
      setLanguages(langs);
      
      const catKeys = Object.keys(catRes.data);
      if (catKeys.length > 0 && !catKeys.includes(activeCategory)) {
        setActiveCategory(catKeys[0]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Nie udało się pobrać tłumaczeń');
    } finally {
      setLoading(false);
    }
  };

  const handleTranslationChange = (key, langCode, value) => {
    setEditedTranslations(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || translations.find(t => t.key === key)?.translations || {}),
        [langCode]: value
      }
    }));
  };

  const handleSave = async (key) => {
    if (!editedTranslations[key]) return;
    
    try {
      setSaving(true);
      await axios.put(
        `${API}/api/translations/${encodeURIComponent(key)}`,
        { translations: editedTranslations[key] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTranslations(prev => prev.map(t => 
        t.key === key ? { ...t, translations: editedTranslations[key] } : t
      ));
      
      setEditedTranslations(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      
      toast.success('Tłumaczenie zapisane');
    } catch (error) {
      toast.error('Nie udało się zapisać');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    const keysToSave = Object.keys(editedTranslations);
    if (keysToSave.length === 0) {
      toast.info('Brak zmian do zapisania');
      return;
    }
    
    try {
      setSaving(true);
      for (const key of keysToSave) {
        await axios.put(
          `${API}/api/translations/${encodeURIComponent(key)}`,
          { translations: editedTranslations[key] },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      await fetchData();
      setEditedTranslations({});
      toast.success(`Zapisano ${keysToSave.length} tłumaczeń`);
    } catch (error) {
      toast.error('Nie udało się zapisać wszystkich zmian');
    } finally {
      setSaving(false);
    }
  };

  const handleInitialize = async () => {
    try {
      const res = await axios.post(`${API}/api/translations/init`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Dodano ${res.data.added} nowych tłumaczeń`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Błąd inicjalizacji');
    }
  };

  const toggleExpand = (key) => {
    setExpandedKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const filteredTranslations = translations.filter(t => {
    if (t.category !== activeCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesKey = t.key.toLowerCase().includes(query);
      const matchesValue = Object.values(t.translations || {}).some(
        v => v?.toLowerCase().includes(query)
      );
      return matchesKey || matchesValue;
    }
    return true;
  });

  const getCompletionStatus = (translation) => {
    const filled = languages.filter(l => translation.translations?.[l.code]?.trim()).length;
    return { filled, total: languages.length, complete: filled === languages.length };
  };

  const getCurrentValue = (key, langCode) => {
    if (editedTranslations[key]?.[langCode] !== undefined) {
      return editedTranslations[key][langCode];
    }
    return translations.find(t => t.key === key)?.translations?.[langCode] || '';
  };

  const hasChanges = (key) => editedTranslations[key] !== undefined;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const categoryKeys = Object.keys(categories);
  const totalChanges = Object.keys(editedTranslations).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Languages className="w-7 h-7 text-primary" />
            Tłumaczenia
          </h1>
          <p className="text-gray-400 mt-1">
            Zarządzaj tłumaczeniami interfejsu. Wszystkie języki (w tym nieaktywne) są dostępne do edycji.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleInitialize} className="border-white/20">
            <RefreshCw className="w-4 h-4 mr-2" />
            Synchronizuj
          </Button>
          {totalChanges > 0 && (
            <Button onClick={handleSaveAll} disabled={saving} className="bg-primary">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Zapisz wszystkie ({totalChanges})
            </Button>
          )}
        </div>
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-gray-400 text-sm">Dostępne języki:</span>
            {languages.map(lang => (
              <div 
                key={lang.code} 
                className={`flex items-center gap-1.5 px-2 py-1 rounded text-sm ${
                  lang.isActive ? 'bg-primary/20 text-primary' : 'bg-white/5 text-gray-500'
                }`}
              >
                <span><FlagIcon code={lang.code} size={16} /></span>
                <span>{lang.name}</span>
                {!lang.isActive && <span className="text-xs">(nieaktywny)</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Szukaj tłumaczeń..."
          className="pl-10 bg-white/5 border-white/10 text-white"
        />
      </div>

      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="bg-white/5 border-white/10 flex-wrap h-auto gap-1 p-1">
          {categoryKeys.map(catKey => {
            const catTranslations = translations.filter(t => t.category === catKey);
            const incomplete = catTranslations.filter(t => !getCompletionStatus(t).complete).length;
            
            return (
              <TabsTrigger key={catKey} value={catKey} className="data-[state=active]:bg-primary relative">
                {categories[catKey]}
                {incomplete > 0 && (
                  <span className="ml-1.5 w-5 h-5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs flex items-center justify-center">
                    {incomplete}
                  </span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {categoryKeys.map(catKey => (
          <TabsContent key={catKey} value={catKey} className="mt-6 space-y-3">
            {filteredTranslations.length === 0 ? (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="py-8 text-center text-gray-500">
                  Brak tłumaczeń w tej kategorii
                </CardContent>
              </Card>
            ) : (
              filteredTranslations.map(translation => {
                const { filled, total, complete } = getCompletionStatus(translation);
                const isExpanded = expandedKeys.has(translation.key);
                const changed = hasChanges(translation.key);
                
                return (
                  <Card 
                    key={translation.key} 
                    className={`bg-white/5 border-white/10 ${changed ? 'ring-1 ring-primary/50' : ''}`}
                  >
                    <CardContent className="p-0">
                      <div 
                        className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
                        onClick={() => toggleExpand(translation.key)}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <code className="text-xs px-2 py-1 rounded bg-black/30 text-gray-400 font-mono">
                            {translation.key}
                          </code>
                          <span className="text-white truncate">
                            {getCurrentValue(translation.key, 'pl') || <span className="text-gray-500 italic">brak</span>}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`flex items-center gap-1.5 text-xs ${complete ? 'text-green-400' : 'text-yellow-400'}`}>
                            {complete ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                            {filled}/{total}
                          </div>
                          {changed && (
                            <Button
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleSave(translation.key); }}
                              disabled={saving}
                              className="bg-primary h-7 px-2"
                            >
                              <Save className="w-3 h-3" />
                            </Button>
                          )}
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-white/10 p-4 space-y-3">
                          {languages.map(lang => (
                            <div key={lang.code} className="flex items-start gap-3">
                              <div className="flex items-center gap-2 w-28 flex-shrink-0 pt-2">
                                <FlagIcon code={lang.code} size={18} />
                                <span className={`text-sm ${lang.isActive ? 'text-white' : 'text-gray-500'}`}>
                                  {lang.code.toUpperCase()}
                                </span>
                              </div>
                              <Input
                                value={getCurrentValue(translation.key, lang.code)}
                                onChange={(e) => handleTranslationChange(translation.key, lang.code, e.target.value)}
                                placeholder={`Tłumaczenie ${lang.name}`}
                                className={`flex-1 bg-black/30 border-white/10 ${
                                  !getCurrentValue(translation.key, lang.code) ? 'border-yellow-500/30' : ''
                                }`}
                              />
                            </div>
                          ))}
                          
                          {translation.isSystemKey && (
                            <p className="text-xs text-gray-500 mt-2">
                              <Globe className="w-3 h-3 inline mr-1" />
                              Klucz systemowy - nie można usunąć
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default AdminTranslations;
